import React, { useState } from 'react'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import styled from 'styled-components'
import DownloadIcon from '@mui/icons-material/Download'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { IconButton, Tooltip } from '@mui/material'
import ReportingFilters from './components/ReportingFilters'
import ReportCommuterTable from './components/ReportCommuterTable'
import ScheduledReports from './components/ScheduledReports'
import AiReportModal from './components/AiReportModal'
import { AppState } from 'redux/config/store'
import { useSelector } from 'react-redux'
import { reportsActions } from './ReportsActions'
import { useDispatch } from 'react-redux'
import { ReportType } from './types'
import { YearMonth } from 'core-system/MonthRangePicker/DropdownCalendar'
import { SegmentListType } from 'features/Programs/Shared/types'
import { getMoment } from './helperFunction'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import { ReportGettingType } from './types'
import SegmentService from 'redux/config/services/SegmentService'
import { reportsReducers } from './ReportsSlice'
import palette from 'core-system/Themes/palette'
import { useGateValue } from '@statsig/react-bindings'

const Container = styled.div``

const TopButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
  gap: 1rem;
`

const AiReportIconButton = styled(IconButton)`
  color: ${palette.primary.pink800};
  border: 1px solid ${palette.primary.pink800};
  border-radius: 0.375rem;
  width: 2.5rem;
  height: 2.5rem;
`

const ReportingView = React.memo(() => {
  const dispatch = useDispatch()
  const {
    reportType,
    startDate,
    endDate,
    selectedSegments,
    tableData,
    tableMessage,
    isTableLoading,
    headerCells,
    isFileDownloading,
    isAiReportLoading,
    aiReportError,
  } = useSelector((state: AppState) => state.reports)
  const { segmentsMap } = useSelector((state: AppState) => state.employer)
  const [isAiModalOpen, setIsAiModalOpen] = useState(false)
  const isAIConciergeEnabled = useGateValue('ai_concierge')

  const startMoment = () => getMoment(startDate).startOf('month')
  const endMoment = () => {
    if (endDate) {
      return getMoment(endDate).add(1, 'month')
    }
    return getMoment(startDate).add(1, 'month')
  }

  const getReports = (reportGettingType: ReportGettingType) => {
    if (reportType === 'usage') {
      dispatch(
        reportsActions.getOverallReport({
          startDate: startMoment().format('YYYY-MM-DD'),
          endDate: endMoment().format('YYYY-MM-DD'),
          segments: selectedSegments.map((segment) => segment.id),
          reportType: reportType,
          reportGettingType,
        })
      )
    } else {
      dispatch(
        reportsActions.getRemainingReports({
          reportType: reportType,
          startDate: startMoment().format(),
          endDate: endMoment().format(),
          segments: selectedSegments.map((segment) => segment.id),
          reportGettingType,
        })
      )
    }
    SegmentService.track(`generate-${reportType}-report`, {
      selectedSegments: selectedSegments.map((segment) => segment.id),
      startDate: startMoment().format('YYYY-MM-DD'),
      endDate: endMoment().format('YYYY-MM-DD'),
    })
  }

  const buttonsDisabled = () => {
    if (!reportType) {
      return true
    }
    if (selectedSegments.length === 0) {
      return true
    }
    if (!startDate) {
      return true
    }
    if (isTableLoading) {
      return true
    }
    return false
  }

  return (
    <Container>
      <TopButtonContainer>
        {isAIConciergeEnabled && (
          <Tooltip title='AI Report'>
            <AiReportIconButton
              onClick={() => {
                dispatch(reportsActions.clearAiReportError())
                setIsAiModalOpen(true)
              }}
              disabled={isTableLoading || isAiReportLoading}
              aria-label='AI Report'
            >
              <AutoAwesomeIcon />
            </AiReportIconButton>
          </Tooltip>
        )}
        <SecondaryButton
          onClick={() => getReports('json')}
          disabled={buttonsDisabled()}
        >
          Generate Report
        </SecondaryButton>
        {/* <PrimaryButton
          startIcon={<DownloadIcon />}
          onClick={() => getReports('csv')}
          disabled={buttonsDisabled()}
          loading={isFileDownloading}
        >
          Download Report
        </PrimaryButton> */}
      </TopButtonContainer>
      <ReportingFilters
        selectedReportType={reportType}
        selectedStartDate={startDate}
        selectedEndDate={endDate}
        selectedSegments={selectedSegments}
        changeReportType={(reportType: ReportType) =>
          dispatch(reportsActions.changeReportType(reportType))
        }
        changeStartDate={(startDate: YearMonth) =>
          dispatch(reportsActions.changeStartDate(startDate))
        }
        changeEndDate={(endDate: YearMonth) =>
          dispatch(reportsActions.changeEndDate(endDate))
        }
        changeSegments={(segments: SegmentListType[]) =>
          dispatch(reportsActions.changeSelectedSegments(segments))
        }
        isTableLoading={isTableLoading}
      />
      <ReportCommuterTable
        tableData={tableData}
        tableMessage={tableMessage}
        isTableLoading={isTableLoading}
        headerCells={headerCells}
        segmentsMap={segmentsMap}
        setNoCommutersTableOperationsMessage={() =>
          dispatch(reportsReducers.setTableMessage('No Data Found'))
        }
      />
      <ScheduledReports />
      {isAIConciergeEnabled && (
        <AiReportModal
          open={isAiModalOpen}
          onClose={() => setIsAiModalOpen(false)}
          onSubmit={(prompt) => {
            dispatch(reportsActions.generateAiReport({ prompt }))
            SegmentService.track('generate-ai-report', {
              promptLength: prompt.length,
            })
          }}
          isLoading={isAiReportLoading}
          error={aiReportError}
        />
      )}
    </Container>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  ReportingView.displayName = 'ReportingView'
}
export default ReportingView
